import { useLanguage } from "@/contexts/LanguageContext";
import { useFadeIn } from "@/hooks/useFadeIn";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQSection = () => {
  const { t } = useLanguage();
  const { ref: headerRef, isVisible: headerVisible } = useFadeIn();
  const { ref: accordionRef, isVisible: accordionVisible } = useFadeIn({ delay: 200 });

  const faqs = [
    { questionKey: "faq.q1.question", answerKey: "faq.q1.answer" },
    { questionKey: "faq.q2.question", answerKey: "faq.q2.answer" },
    { questionKey: "faq.q3.question", answerKey: "faq.q3.answer" },
    { questionKey: "faq.q4.question", answerKey: "faq.q4.answer" },
    { questionKey: "faq.q5.question", answerKey: "faq.q5.answer" },
    { questionKey: "faq.q6.question", answerKey: "faq.q6.answer" },
    { questionKey: "faq.q7.question", answerKey: "faq.q7.answer" },
    { questionKey: "faq.q8.question", answerKey: "faq.q8.answer" },
  ];

  // Split into two columns
  const midpoint = Math.ceil(faqs.length / 2);
  const leftColumn = faqs.slice(0, midpoint);
  const rightColumn = faqs.slice(midpoint);

  const renderColumn = (items: typeof faqs, columnOffset: number) => (
    <Accordion type="single" collapsible className="space-y-3">
      {items.map((faq, index) => (
        <AccordionItem
          key={faq.questionKey}
          value={`item-${columnOffset + index}`}
          className="bg-white/70 backdrop-blur-sm rounded-xl border border-border/50 px-5 overflow-hidden shadow-sm data-[state=open]:shadow-md transition-shadow duration-300"
        >
          <AccordionTrigger className="py-4 text-left hover:no-underline group">
            <span className="font-heading font-semibold text-foreground text-sm md:text-base pr-4 group-hover:text-gold transition-colors">
              {t(faq.questionKey)}
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-4 text-muted-foreground text-sm leading-relaxed">
            {t(faq.answerKey)}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );

  return (
    <section className="py-24 md:py-32 bg-[hsl(var(--secondary))]/30">
      <div className="container-custom">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center max-w-2xl mx-auto mb-12 fade-up ${headerVisible ? 'visible' : ''}`}
        >
          <span className="eyebrow-text">
            {t("faq.label")}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mt-3 mb-4">
            {t("faq.title")}
          </h2>
          <p className="text-muted-foreground">
            {t("faq.subtitle")}
          </p>
        </div>

        {/* FAQ 2-Column Grid */}
        <div
          ref={accordionRef}
          className={`max-w-6xl mx-auto grid md:grid-cols-2 gap-6 fade-up ${accordionVisible ? 'visible' : ''}`}
        >
          {renderColumn(leftColumn, 0)}
          {renderColumn(rightColumn, midpoint)}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
